import { safeGetValue } from '@atlassian/jira-business-fields/utils';
import { ff } from '@atlassian/jira-feature-flagging';
import { OPTIMISTIC_ISSUE_ID, GROUP_ITEM_PREFIX, ROOT_LEVEL } from '../../constants';

type BaseIssueNode = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly issueId: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly fields?: ReadonlyArray<any> | null;
};

/**
 * This function will add a new subtask locally into a node
 * This is useful when the consumer does not consider the content of subtasks itself but only whether there are any subtasks
 */
export const addSubtaskToParent = <
	SubtaskFieldIssueType,
	NodeType extends {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		readonly fields?: ReadonlyArray<any> | null;
	},
>(
	parentIssue: NodeType,
	subtaskFieldIssue: SubtaskFieldIssueType,
): NodeType => {
	const parentIssueFields = parentIssue.fields || [];
	const subtasksFieldIndex = parentIssueFields.findIndex((field) => field?.fieldKey === 'subtasks');

	const subtasksField =
		typeof subtasksFieldIndex === 'number' && subtasksFieldIndex !== -1
			? parentIssueFields[subtasksFieldIndex]
			: undefined;

	if (subtasksFieldIndex === -1 || subtasksField == null) {
		// there should be a subtask field always in the code
		// since it's declared in the gql
		return parentIssue;
	}

	const updatedFields = [...parentIssueFields];

	updatedFields[subtasksFieldIndex] = {
		...updatedFields[subtasksFieldIndex],
		issues: [subtaskFieldIssue, ...(subtasksField?.issues || [])].filter(
			(issue, index, issueArray) =>
				issueArray.findIndex((current) => current.issueId === issue.issueId) === index,
		),
	};

	return {
		...parentIssue,
		// we're updating just the subtasks field, but flow doesn't understand that because the union type of fields
		fields: updatedFields,
	};
};

export const isChildCreateEnabled = (isFiltering: boolean, id: number): boolean => {
	if (id === OPTIMISTIC_ISSUE_ID) {
		return false;
	}

	return !isFiltering;
};

export const getSameProjectParentIssueId = (node: BaseIssueNode): string | undefined => {
	const issueKey = safeGetValue(node.fields, 'issuekey', 'text', undefined);
	const parentIssue = safeGetValue<{ key: string; issueId: string } | undefined>(
		node.fields,
		'parent',
		'parentIssue',
		undefined,
	);

	const projectKey1 = (issueKey ?? '').split('-')[0];
	const projectKey2 = (parentIssue?.key ?? '').split('-')[0];
	const sameProject = projectKey1 && projectKey2 ? projectKey1 === projectKey2 : false;

	return sameProject ? parentIssue?.issueId : undefined;
};

export const getParentIssueId = (node: BaseIssueNode): string | undefined => {
	if (ff('list-uses-new-jql-parent-project-directive_v5gfs')) {
		return getSameProjectParentIssueId(node);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const parentIssue = safeGetValue<any>(node.fields, 'parent', 'parentIssue', undefined);

	if (parentIssue?.issueId) {
		return parentIssue?.issueId;
	}

	return undefined;
};

export const isGroupItem = (issueId: string | null = '') =>
	typeof issueId === 'string' && issueId.startsWith(GROUP_ITEM_PREFIX);

export const getGroupItemId = (issueId: string): string => {
	const [_, groupItemId] = issueId.split(`${GROUP_ITEM_PREFIX}_`);
	return groupItemId ?? issueId;
};

export const isRootLevel = (issueId?: string): boolean => {
	if (ff('jwm-list-view-update-group-count_36voh')) {
		return issueId === ROOT_LEVEL;
	}

	if (!issueId) {
		return false;
	}

	return issueId === ROOT_LEVEL || isGroupItem(issueId);
};
